import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
// AdminSystem es el shell (sidebar + auth + Outlet) → eager, se ve en cada página.
import { AdminSystem } from './pages/AdminSystem'
import { ProtectedRoute } from './components/ProtectedRoute'
import './App.css'

// ── Páginas lazy: cada una es su propio chunk, se descarga/parsea sólo al navegar.
// Esto saca del bundle inicial recharts + todo el código de las 18 páginas → carga
// inicial mucho más liviana. (Los named exports se re-mapean a { default }.)
const SettingsPage        = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ResetPassword       = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })))
const Dashboard           = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const SpeechAnalyticsView = lazy(() => import('./pages/SpeechAnalyticsView').then(m => ({ default: m.SpeechAnalyticsView })))
const CampaignsView       = lazy(() => import('./pages/CampaignsView').then(m => ({ default: m.CampaignsView })))
const ReportsView         = lazy(() => import('./pages/ReportsView').then(m => ({ default: m.ReportsView })))
const OperatorsView       = lazy(() => import('./pages/OperatorsView').then(m => ({ default: m.OperatorsView })))
const AuditorsView        = lazy(() => import('./pages/AuditorsView').then(m => ({ default: m.AuditorsView })))
const PhraseSearchView    = lazy(() => import('./pages/PhraseSearchView').then(m => ({ default: m.PhraseSearchView })))
const EstadosCrmView      = lazy(() => import('./pages/EstadosCrmView').then(m => ({ default: m.EstadosCrmView })))
const BillingView         = lazy(() => import('./pages/BillingView').then(m => ({ default: m.BillingView })))
const AuditLogView        = lazy(() => import('./pages/AuditLogView').then(m => ({ default: m.AuditLogView })))
const ConnectorsView      = lazy(() => import('./pages/connectors/ConnectorsView').then(m => ({ default: m.ConnectorsView })))
const CreateVoicebotView  = lazy(() => import('./pages/connectors/CreateVoicebotView').then(m => ({ default: m.CreateVoicebotView })))
const InboxView           = lazy(() => import('./pages/inbox/InboxView').then(m => ({ default: m.InboxView })))
const CRMView             = lazy(() => import('./pages/CRMView').then(m => ({ default: m.CRMView })))
const WikiView            = lazy(() => import('./pages/WikiView').then(m => ({ default: m.WikiView })))
const MonitoringView      = lazy(() => import('./pages/MonitoringView').then(m => ({ default: m.MonitoringView })))
const ContactDetailView   = lazy(() => import('./pages/ContactDetailView').then(m => ({ default: m.ContactDetailView })))

function App() {
  return (
    <Suspense fallback={
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
        <div style={{ width:32, height:32, border:'3px solid #e5e7eb', borderTopColor:'#2c92e6', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      </div>
    }>
    <Routes>
      <Route path="/" element={<AdminSystem />}>
        <Route index element={<Navigate to="inicio" replace />} />
        <Route path="inicio" element={<Dashboard />} />

        {/* Speech Analytics — requiere view_analytics */}
        <Route path="speech-analytics" element={
          <ProtectedRoute permissions={['view_analytics']}>
            <SpeechAnalyticsView />
          </ProtectedRoute>
        } />

        {/* Campañas — requiere algún permiso de campañas */}
        <Route path="campaigns" element={
          <ProtectedRoute permissions={['view_campaigns', 'add_campaigns', 'update_campaigns', 'delete_campaigns']}>
            <CampaignsView />
          </ProtectedRoute>
        } />

        {/* Reportes — requiere algún permiso de campañas */}
        <Route path="reports" element={
          <ProtectedRoute permissions={['view_campaigns', 'add_campaigns', 'update_campaigns', 'delete_campaigns', 'view_reports']}>
            <ReportsView />
          </ProtectedRoute>
        } />

        {/* Voicebot — ahora es un tipo de campaña; se gestiona desde /campaigns */}
        <Route path="voicebot" element={<Navigate to="/campaigns" replace />} />

        {/* Resumen de operadores — requiere view_operators */}
        <Route path="operators" element={
          <ProtectedRoute permissions={['view_operators']}>
            <OperatorsView />
          </ProtectedRoute>
        } />

        {/* Desempeño de auditores — propio o todos según permiso */}
        <Route path="auditors" element={
          <ProtectedRoute permissions={['view_auditor_metrics', 'view_all_auditor_metrics']}>
            <AuditorsView />
          </ProtectedRoute>
        } />

        {/* Buscador de frases en transcripciones */}
        <Route path="phrase-search" element={
          <ProtectedRoute permissions={['view_phrase_search', 'view_reports', 'view_analytics']}>
            <PhraseSearchView />
          </ProtectedRoute>
        } />

        {/* Monitoreo (Asterisk / Prometheus) */}
        <Route path="monitoring" element={
          <ProtectedRoute permissions={['view_monitoring']}>
            <MonitoringView />
          </ProtectedRoute>
        } />

        {/* Wiki / documentación */}
        <Route path="wiki" element={
          <ProtectedRoute permissions={['view_wiki', 'manage_wiki']}>
            <WikiView />
          </ProtectedRoute>
        } />
        {/* Enlace directo a un artículo: /wiki/facturacion-cliente. El slug ya
            existía en la base y es legible, así que el link que se le manda a
            alguien dice de qué es antes de abrirlo. */}
        <Route path="wiki/:slug" element={
          <ProtectedRoute permissions={['view_wiki', 'manage_wiki']}>
            <WikiView />
          </ProtectedRoute>
        } />

        {/* Estados CRM (mapeo voicebot → estado de gestión) */}
        <Route path="estados-crm" element={
          <ProtectedRoute permissions={['manage_estados_crm']}>
            <EstadosCrmView />
          </ProtectedRoute>
        } />

        {/* Conectores de canal — alcanza con cualquiera de los permisos del apartado */}
        <Route path="connectors" element={
          <ProtectedRoute permissions={['view_connectors', 'add_connectors', 'update_connectors', 'delete_connectors', 'manage_connectors']}>
            <ConnectorsView />
          </ProtectedRoute>
        } />
        <Route path="connectors/voicebot/create" element={
          <ProtectedRoute permissions={['create_voicebot', 'manage_connectors']}>
            <CreateVoicebotView />
          </ProtectedRoute>
        } />
        <Route path="connectors/voicebot/:connectorId/edit" element={
          <ProtectedRoute permissions={['update_voicebot', 'create_voicebot', 'manage_connectors']}>
            <CreateVoicebotView />
          </ProtectedRoute>
        } />

        {/* Inbox — requiere view_inbox o admin */}
        <Route path="inbox" element={
          <ProtectedRoute permissions={['view_inbox']}>
            <InboxView />
          </ProtectedRoute>
        } />

        {/* CRM — requiere view_contacts */}
        <Route path="crm" element={
          <ProtectedRoute permissions={['view_contacts']}>
            <CRMView />
          </ProtectedRoute>
        } />
        <Route path="crm/contact/:contactId" element={
          <ProtectedRoute permissions={['view_contacts']}>
            <ContactDetailView />
          </ProtectedRoute>
        } />

        {/* Facturación — requiere view_billing */}
        <Route path="billing" element={
          <ProtectedRoute permissions={['view_billing']}>
            <BillingView />
          </ProtectedRoute>
        } />

        {/* Logs de auditoría — solo manage_permissions */}
        <Route path="audit-logs" element={
          <ProtectedRoute permissions={['manage_permissions']}>
            <AuditLogView />
          </ProtectedRoute>
        } />

        {/* Configuraciones — requiere algún permiso de gestión */}
        <Route path="settings/*" element={
          <ProtectedRoute permissions={[
            'add_profiles', 'update_profiles', 'delete_profiles',
            'view_users', 'add_users', 'update_users', 'delete_users',
            'add_permissions', 'update_permissions', 'delete_permissions', 'manage_permissions',
            'manage_external_services',
          ]}>
            <SettingsPage />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  )
}

export default App